import { z } from 'zod';

export const ResourceDataSchema = z.unknown();

export type ResourceData = z.infer<typeof ResourceDataSchema>;

export const ResourceTypes = ['document', 'group'] as const;

export type ResourceType = (typeof ResourceTypes)[number];

export const BaseResourceMetadataSchema = z
  .object({
    id: z.string().optional(),
    toolCallId: z.string().optional(),
    name: z.string().optional(),
    provider: z.string().optional(),
    resourceType: z.string().optional(),
    status: z.string().optional(),
    documentId: z.string().optional(),
    collectionPath: z.string().optional(),
    documentPath: z.string().optional(),
    documentData: z.unknown().optional(),
    variables: z.record(z.string(), z.unknown()).optional(),
    error: z.string().optional(),
    cause: z.unknown().optional(),
    pushdownFilters: z.array(z.unknown()).optional(),
    query: z.record(z.string(), z.unknown()).optional(),
    resolvedQuery: z.record(z.string(), z.unknown()).optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();

export interface BaseResourceMeta extends z.infer<typeof BaseResourceMetadataSchema> {}

export const ResourceDocMetaSchema = BaseResourceMetadataSchema.extend({ result: ResourceDataSchema.optional() });

export interface ResourceDocMeta extends z.infer<typeof ResourceDocMetaSchema> {}

export const ResourceGroupMetaSchema = BaseResourceMetadataSchema.extend({
  resultsCount: z.number().optional(),
  results: z.array(ResourceDataSchema).optional(),
});

export interface ResourceGroupMeta extends z.infer<typeof ResourceGroupMetaSchema> {}

export const ResourceMetaSchema = z.union([ResourceDocMetaSchema, ResourceGroupMetaSchema]);

export type ResourceMeta = z.infer<typeof ResourceMetaSchema>;

export function extractResourceMeta(metadata?: Record<string, unknown>): ResourceMeta[] {
  const resources: ResourceMeta[] = (metadata?.resources as ResourceMeta[] | undefined) ?? [];
  if (metadata?.resource) {
    resources.push(metadata.resource as ResourceMeta);
  }
  return resources.filter((entry): entry is ResourceMeta => typeof entry === 'object' && !!entry);
}
